import { useMemo } from 'react';
import { QueryContainer } from '@opensearch-project/opensearch/api/_types/_common.query_dsl';
import { CardinalityAggregate } from '@opensearch-project/opensearch/api/_types/_common.aggregations';

import { InitialProductQueryFilters } from '@/constants';

import { Bucket, RefinementListItem } from '../types';
import { useOpenSearchContext } from './useOpenSearchContext';
import {
  buildQueryFilterFromSearchParams,
  transformBucketToRefinementItem,
} from '../utils';
import { useQueryFilterBuilders } from './query_builders';
import { useUpdateSearchParams } from './useUpdateSearchParams';

const useRefinementList = ({ attribute }: { attribute: string }) => {
  const {
    aggregations,
    updateQueryFiltersByFacets,
    queryFiltersByFacets,
    isLoading,
    updateFrom,
  } = useOpenSearchContext();
  const { updateSearchParams } = useUpdateSearchParams();
  const { buildFacetQueryFilters } = useQueryFilterBuilders();

  const effectiveQueryFilters = useMemo(
    () => ({
      ...buildQueryFilterFromSearchParams(InitialProductQueryFilters),
      ...queryFiltersByFacets,
    }),
    [queryFiltersByFacets],
  );

  const refinedValues = useMemo(
    () =>
      new Set(
        (
          (effectiveQueryFilters[attribute] as QueryContainer)?.bool
            ?.should as QueryContainer[]
        )?.map(query => query.match?.[attribute]),
      ),
    [effectiveQueryFilters, attribute],
  );

  const refinementListItems: RefinementListItem[] = useMemo(
    () =>
      aggregations?.facets?.[attribute]?.values?.buckets?.map(
        (bucket: Bucket & { maxDocCount: CardinalityAggregate }) => ({
          ...transformBucketToRefinementItem(bucket, attribute),
          isRefined:
            refinedValues.has(bucket.key) ||
            (!!bucket.key_as_string &&
              refinedValues.has(JSON.parse(bucket.key_as_string))),
        }),
      ) ?? [],
    [aggregations, refinedValues],
  );

  const handleRefine = (value: string | number | boolean) => {
    const newRefined = new Set(refinedValues);
    if (refinedValues.has(value)) {
      newRefined.delete(value);
    } else {
      newRefined.add(value);
    }

    updateSearchParams({
      [attribute]: Array.from(newRefined).map(String),
      page: '1',
    });

    updateFrom(0);
    updateQueryFiltersByFacets((prev: Record<string, QueryContainer>) => ({
      ...prev,
      [attribute]: buildFacetQueryFilters(
        attribute,
        Array.from(newRefined.values()),
      ),
    }));
  };

  const clearAll = () => {
    updateSearchParams({ [attribute]: null });

    updateQueryFiltersByFacets((prev: Record<string, QueryContainer>) => ({
      ...prev,
      [attribute]: buildFacetQueryFilters(attribute, []),
    }));
  };

  return {
    items: refinementListItems,
    refine: handleRefine,
    clearAll,
    isLoading,
  };
};

export { useRefinementList };
